import React, { useEffect, useState,useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useLazyQuery } from '@apollo/client';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Plot from 'react-plotly.js';
import { SAMPLES_BY_MATID_QUERY, MATERIALS_QUERY, ECHEMICAL_QUERY, PUBLICATION_QUERY, XRD_QUERY, XPS_QUERY,SAMPLES_BY_PUBID_QUERY } from './query';
import MaterialsTable from './MaterialTable';
import GenericGraph from './GenericGraph';
import ElectrochemicalGraphs from './ElectrochemicalGraphs';
import { FaBook, FaCode, FaComments, FaGithub, FaCreativeCommons } from 'react-icons/fa';
import Header from './Header';
import AdvancedDataGrid from './AdvanceDataGrid';
import AdvanceMaterialTable from './AdvanceMaterialTable';

const Input = (props) => (
  <input {...props} className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" />
);

const Button = ({ children, ...props }) => (
  <button {...props} className="px-4 py-2 bg-red-800 text-white rounded hover:bg-red-800 transition-colors">
    {children}
  </button>
);


const processSampleData = (sample) => {
  try {
    const parsedData = JSON.parse(sample.data);
    return { ...sample, ...parsedData, data: undefined };
  } catch (error) {
    console.error(`Error processing sample ${sample.sampleId}:`, error);
    return sample;
  }
};


const PublicationDetails = () => {
  const { id: pubId } = useParams();
  const [samples, setSamples] = useState([])
  const [processedData, setProcessedData] = useState([]);
  const [graphData, setGraphData] = useState([]);
  const [xrdData, setXrdData] = useState([]);
  const [xpsData, setXpsData] = useState([]);
  const [cvInitialData, setCvInitialData] = useState([]);
  const [cvEndData, setCvEndData] = useState([]);
  const [selectedRowIndices, setSelectedRowIndices] = useState([0]);

  const { loading: publicationLoading, error: publicationError, data: publicationData } = useQuery(PUBLICATION_QUERY, { variables: { pubId } });
  const { loading: materialsLoading, error: materialsError, data: materialsData } = useQuery(MATERIALS_QUERY, { variables: { pubId } });
  const { loading: sampleLoading, error:sampleError, data: sampleData} = useQuery(SAMPLES_BY_PUBID_QUERY, { variables: { pubId } });

  const [getSamples] = useLazyQuery(SAMPLES_BY_MATID_QUERY);
  const [getXrdData] = useLazyQuery(XRD_QUERY);
  const [getXpsData] = useLazyQuery(XPS_QUERY);
  const [getEchemicalData] = useLazyQuery(ECHEMICAL_QUERY);
  const defaultVisibleColumns = ['composition', 'morphology', 'reaction','pH', 'onset_potential_(+/-100_\u03BCA/cm2)', 'replicate', 'onset_potential_mean_std(+/-0.1_mA/cm2)'];

  const handleSelectedRowsChange = useCallback((selectedIndices) => {
    setSelectedRowIndices(selectedIndices);
  }, []);

  const countReactions = () => {
    const counts = {
      OER: 0,
      ORR: 0
    };
    
    samples.forEach(item => {
      if (item.reaction === 'OER') counts.OER++;
      if (item.reaction === 'ORR') counts.ORR++;
    });
    
    return counts;
  };

  useEffect(() => {
    if (sampleData && sampleData.samples) {
      const extractedSamples = sampleData.samples.edges.map(edge => {
        const { sampleId, matId, data } = edge.node;
        // Parse the data string into an object
        const parsedData = JSON.parse(data);
        parsedData["matId"] = matId;
        parsedData["sampleId"] = sampleId;
        parsedData["onset_potential_(+/-100_\u03BCA/cm2)"] =  Number(parsedData["onset_potential(+/-0.1_mA/cm2)"]).toFixed(2);
        parsedData["onset_potential_(+/-1000_\u03BCA/cm2)"] =  Number(parsedData["onset_potential(+/-1_mA/cm2)"]).toFixed(2);
        parsedData["onset_potential_(+/-10,000_\u03BCA/cm2)"] =  Number(parsedData["onset_potential(+/-10_mA/cm2)"]).toFixed(2);
        parsedData["onset_potential_(+/-50_\u03BCA/cm2)"] =  Number(parsedData["onset_potential(+/-0.05_mA/cm2)"]).toFixed(2);
        parsedData["onset_potential_(+/-10_\u03BCA/cm2)"] =  Number(parsedData["onset_potential(+/-0.01_mA/cm2)"]).toFixed(2);
     
        delete parsedData["onset_potential(+/-0.1_mA/cm2)"];
        delete parsedData["onset_potential(+/-1_mA/cm2)"];
        delete parsedData["onset_potential(+/-10_mA/cm2)"];
        delete parsedData["onset_potential(+/-0.05_mA/cm2)"];
        delete parsedData["onset_potential(+/-0.01_mA/cm2)"];
        
        const sortedParsedData = Object.fromEntries(Object.entries(parsedData).sort(([a], [b]) => a.localeCompare(b)));

        // Create an ordered object based on defaultVisibleColumns
        let orderedData = {};
        
        // First add the keys from defaultVisibleColumns in specified order
        defaultVisibleColumns.forEach(key => {
          if (key === 'sampleId') {
            orderedData[key] = sampleId;
          } else {
            orderedData[key] = sortedParsedData[key];
          }
        });


        
        // Add remaining keys that are not in defaultVisibleColumns
        Object.keys(sortedParsedData).forEach(key => {
          if (!defaultVisibleColumns.includes(key)) {
            orderedData[key] = sortedParsedData[key];
          }
        });
        
        return orderedData;
      });
      
      setSamples(extractedSamples);
    }
  }, [sampleData]);

  useEffect(() => {
    const fetchAndProcessSamples = async () => {
      if (materialsData?.materials?.edges) {
        const processedMaterials = await Promise.all(
          materialsData.materials.edges.map(async ({ node: material }) => {
            try {
              const { data } = await getSamples({ variables: { matId: parseInt(material.matId) } });
              const processedSamples = data.samples.edges.map(({ node }) => processSampleData(node));
              return { ...material, ...processedSamples[0] };
            } catch (error) {
              console.error(`Error fetching samples for material ${material.matId}:`, error);
              return material;
            }
          })
        );
         // Sort processedMaterials based on onset_potential(+/-0.1_mA/cm2)
        const sortedMaterials = processedMaterials.sort((a, b) => {
          const onsetA = a["onset_potential(+/-0.1_mA/cm2)"];
          const onsetB = b["onset_potential(+/-0.1_mA/cm2)"];
          
          // Handle cases where the value might be undefined or not a number
          if (onsetA === undefined) return 1;
          if (onsetB === undefined) return -1;
          return onsetA - onsetB;
      });
    

        setProcessedData(sortedMaterials);
      }
    };
    if (!materialsLoading && !materialsError) {
      fetchAndProcessSamples();
    }
  }, [materialsData, materialsLoading, materialsError, getSamples]);

  useEffect(() => {
    const fetchXrdAndXpsData = async () => {
      if (samples.length > 0 && selectedRowIndices.length>0) {
        const materials = selectedRowIndices.map(index => samples[index]);;
        const [xrdResults, xpsResults] = await Promise.all([
          Promise.all(materials.map(material => getXrdData({ variables: { matId: parseInt(material.matId) } }))),
          Promise.all(materials.map(material => getXpsData({ variables: { matId: parseInt(material.matId) } })))
        ]);
  
        // Process XRD data with normalized intensity
        const normalizedXrdData = xrdResults.flatMap((result, index) =>
          result.data.xrd.edges.map(({ node }) => {
            // Find max intensity for this node
            const maxIntensity = Math.max(...node.intensity);
            const minIntensity =  Math.min(...node.intensity);
            
            // Create normalized intensity array
            const normalizedIntensity = node.intensity.map(value => (value - minIntensity)  / maxIntensity);
            
            return {
              ...node,
              material: (selectedRowIndices[index]+1) + ". "+ materials[index].composition,
              intensity: normalizedIntensity
            };
          })
        );
  
        // Process XPS data with normalized intensity
        const normalizedXpsData = xpsResults.flatMap((result, index) =>
          result.data.xps.edges.map(({ node }) => {
            // Find max intensity for this node
            const maxIntensity = Math.max(...node.intensity);
            const minIntensity =  Math.min(...node.intensity);
            
            // Create normalized intensity array
            const normalizedIntensity = node.intensity.map(value => (value - minIntensity) / maxIntensity);
            
            return {
              ...node,
              material: (selectedRowIndices[index]+1) + ". "+ materials[index].composition,
              intensity: normalizedIntensity
            };
          })
        );
  
        setXrdData(normalizedXrdData);
        setXpsData(normalizedXpsData);
      }
    };
    
    if (!materialsLoading && !materialsError) {
      fetchXrdAndXpsData();
    }
  }, [materialsData, materialsLoading, materialsError, getXrdData, getXpsData, samples, selectedRowIndices]);
  
  useEffect(() => {
    if (samples.length > 0) {
      var counts = countReactions();
      var isORRGreater = true;
      if (counts.OER > counts.ORR){
        isORRGreater = false;
      }

      // Add index before filtering
      var samplesWithIndex = samples.map((item, index) => ({
        ...item,
        originalIndex: index +1 // This preserves the original position
      }));
      
      // Filter samples based on reaction type
      var filteredSamples = samplesWithIndex.filter(item => 
        isORRGreater ? item.reaction === "ORR" : item.reaction === "OER"
      );

      var tempGraphData = filteredSamples.map((item) => ({
        compound: item.composition,
        originalIndex: item.originalIndex,
        "onset_potential(+/-0.01_mA/cm2)": item["onset_potential_(+/-10_\u03BCA/cm2)"],
        "onset_potential(+/-0.05_mA/cm2)": item["onset_potential_(+/-50_\u03BCA/cm2)"],
        "onset_potential(+/-0.1_mA/cm2)": item["onset_potential_(+/-100_\u03BCA/cm2)"],
      }));
      
      const groupedData = tempGraphData.reduce((acc, item) => {
        const compound = item.compound;
        
        if (isORRGreater){
          if (!acc[compound]) {
            acc[compound] = {
              compound : item.originalIndex + "." + compound,
              'onset_potential(+/-0.01_mA/cm2)': -Infinity,
              'onset_potential(+/-0.05_mA/cm2)': -Infinity,
              'onset_potential(+/-0.1_mA/cm2)': -Infinity
            };
          }
          if (parseFloat(acc[compound]['onset_potential(+/-0.1_mA/cm2)']) < parseFloat(item['onset_potential(+/-0.1_mA/cm2)'])){
              acc[compound]['onset_potential(+/-0.1_mA/cm2)'] = parseFloat(item['onset_potential(+/-0.1_mA/cm2)']);
              acc[compound]['onset_potential(+/-0.01_mA/cm2)'] = parseFloat(item['onset_potential(+/-0.01_mA/cm2)']);
              acc[compound]['onset_potential(+/-0.05_mA/cm2)'] = parseFloat(item['onset_potential(+/-0.05_mA/cm2)']);
          }
          
        }else{

          if (!acc[compound]) {
            acc[compound] = {
              compound : item.originalIndex + "." + compound,
              'onset_potential(+/-0.01_mA/cm2)': Infinity,
              'onset_potential(+/-0.05_mA/cm2)': Infinity,
              'onset_potential(+/-0.1_mA/cm2)': Infinity
            };
          }

          if (parseFloat(acc[compound]['onset_potential(+/-0.1_mA/cm2)']) > parseFloat(item['onset_potential(+/-0.1_mA/cm2)'])){
            acc[compound]['onset_potential(+/-0.1_mA/cm2)'] = parseFloat(item['onset_potential(+/-0.1_mA/cm2)']);
            acc[compound]['onset_potential(+/-0.01_mA/cm2)'] = parseFloat(item['onset_potential(+/-0.01_mA/cm2)']);
            acc[compound]['onset_potential(+/-0.05_mA/cm2)'] = parseFloat(item['onset_potential(+/-0.05_mA/cm2)']);
        }

        }
        
    
        return acc;
      }, {});

       // Sort processedMaterials based on onset_potential(+/-0.1_mA/cm2)
       const sortedMaterials = Object.values(groupedData).sort((a, b) => {
        const onsetA = a["onset_potential(+/-0.1_mA/cm2)"];
        const onsetB = b["onset_potential(+/-0.1_mA/cm2)"];
        
        // Handle cases where the value might be undefined or not a number
        if (onsetA === undefined) return 1;
        if (onsetB === undefined) return -1;
        return onsetA - onsetB;
      });
      
      setGraphData(sortedMaterials);
    }

  }, [processedData]);


  useEffect(() => {
    const fetchCVData = async () => {
      if (samples.length > 0 && selectedRowIndices.length) {
        
        const selectedSamples = selectedRowIndices.map(index => samples[index]);

        const cvResults = await Promise.all(selectedSamples.map(material =>
          getEchemicalData({ variables: { sampleId: parseInt(material.sampleId, 10) } })
        ));

        const initialCVData = [];
        const endCVData = [];

        cvResults.forEach((result, index) => {
          const material = selectedSamples[index];
          if (result?.data?.echemical?.edges) {
            result.data.echemical.edges.forEach(({ node }) => {
            
              const min_value = Math.min(node.potential);
              const min_value_index = node.potential.indexOf(min_value);
              var max_value = Math.min(node.potential);
              const max_value_index = node.potential.indexOf(max_value);
              var local_max_min_index = (node.current.length)/2;
              var firstPartValue = "Forward sweep";
              var secondaPartValue = "Reverse sweep";
              if(min_value < node.potential[0] && min_value < node.potential[node.potential.length-1]){
                local_max_min_index = min_value_index;
                firstPartValue = "Reverse sweep";
                secondaPartValue = "Forward sweep";
              }  
  
              if(max_value > node.potential[0] && max_value > node.potential[node.potential.length-1]){
                local_max_min_index = max_value_index;
                firstPartValue = "Forward sweep";
                secondaPartValue = "Reverse sweep";
              } 
              
              const cvData = { ...node, material: (selectedRowIndices[index]+1) + ". "+ material.composition , mid_index: local_max_min_index, firstPartName: "Forward Sweep",
                secondPartName: "Reverse Sweep"};
  
              if (cvData.cvtype === "CV_initial") {
                initialCVData.push(cvData);
              } else {
                endCVData.push(cvData);
              }
            });
          }
        });

        setCvInitialData(initialCVData);
        setCvEndData(endCVData);
      }
    };

    fetchCVData();
  }, [samples, getEchemicalData, selectedRowIndices]);

  if (publicationLoading || materialsLoading) {
    return <div className="text-center mt-8">Loading...</div>;
  }

  if (publicationError || materialsError) {
    return <div className="text-center mt-8 text-red-800">Error: {publicationError?.message || materialsError?.message}</div>;
  }

  const publication = publicationData.publicationsExp.edges[0].node;


  const formattedAuthors = publication.authors
  .replace(/[\[\]"]/g, '')  // Remove brackets and quotes
  .replace(/\\u([0-9a-fA-F]{4})/g, (match, p1) => String.fromCharCode(parseInt(p1, 16)))  // Convert Unicode escapes
  .split(',')
  .map((author) => author.trim())
  .join(', ');


  return (
    <div className="flex flex-col min-h-screen">
      <nav className="bg-white shadow-md">
        <div className=" px-4 py-2 flex justify-between items-center">
          <div className="px-4 py-2 flex justify-between items-center">
            <Header />
          </div>

          <div className="text-red-800 font-bold">Dataset for {publication.pubId}</div>
          <img src="/images/catalysis-hub-logo.png" alt="Catalysis-Hub Logo" className="w-40 h-16 object-contain" />
        </div>
      </nav>

      <div className="flex-grow bg-gray-100">
        <div className="container mx-auto p-4">
          <h1 className="text-2xl font-bold mb-4 text-center">Viewing dataset: {publication.pubId}</h1>
          <h2 className="text-xl font-bold text-red-800 mb-2 text-center">{publication.title}</h2>
          <p className="text-gray-600 mb-4 text-center">{formattedAuthors}</p>
          <div className="flex justify-center items-center mb-4">
            <span className="inline-flex items-center text-xs font-medium rounded-md overflow-hidden mr-2">
              <span className="bg-gray-800 text-white px-2 py-1">DOI</span>
              <a href={`https://dx.doi.org/${publication.doi}`} target="_blank" rel="noopener noreferrer" className="bg-blue-100 text-blue-800 px-2 py-1 hover:underline">
                {publication.doi}
              </a>
            </span>
          </div>
          <p className="mb-8 text-center">Publication date: {publication.year}</p>

          {/* <div className="mb-8">
            <MaterialsTable data={samples} defaultVisibleColumns={defaultVisibleColumns} onSelectedRowsChange={handleSelectedRowsChange} publicationId={publication.pubId}/>
          </div> */}
          
          <AdvanceMaterialTable data={samples} defaultVisibleColumns={defaultVisibleColumns} onSelectedRowsChange={handleSelectedRowsChange} publicationId={publication.pubId}></AdvanceMaterialTable>
          <div className="mt-10"></div>
          <ElectrochemicalGraphs graphData={graphData} />


          <h3 className="text-xl font-bold mb-4">Characterization</h3>
          <div className="text-lg mb-6">
           X-ray diffraction (XRD) and photoelectron spectroscopy (XPS) results are shown below.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white p-4 rounded-lg shadow-md h-[500px]">
              <GenericGraph data={xrdData} title="XRD" xAxisTitle="2 theta (degree)" yAxisTitle="Intensity" xKey="degree" yKey="intensity" nameKey="material" />
            </div>
            <div className="bg-white p-4 rounded-lg shadow-md h-[500px]">
              <GenericGraph data={xpsData} title="XPS" xAxisTitle="Binding energy (eV)" yAxisTitle="Intensity" xKey="bindingEnergy" yKey="intensity" nameKey="material" />
            </div>
          </div>
          <h3 className="text-xl font-bold mb-4">CV curves </h3>
          <div className="text-lg mb-6">
           Cyclic voltametry (CV) curves are shown below. Select the current density value in the legend below to re-calculate the onset potentials.
          </div>
          <div className="max-w-6xl mx-auto mb-8">
          <div className="bg-white p-4 rounded-lg shadow-md w-[800px] h-[600px] mx-auto">
            <GenericGraph 
              data={cvInitialData} 
              title="CV Curve" 
              xAxisTitle="Potential (V vs. RHE)" 
              yAxisTitle="Current (mA/cm<sup>2</sup>)" 
              xKey="potential" 
              yKey="current" 
              nameKey="material" 
              showIntersection={true}
              initialIntersectY={-0.1}
              showLegend={false}
            />
          </div>
        </div>

        </div>
      </div>

      <footer className="bg-red-800 text-white py-4">
      <div className="container mx-auto px-4 flex justify-between items-center">
        <div className="flex items-center">
          <img 
            src="/images/suncat_logo.png" 
            alt="suncat_logo.png" 
            className="h-8 mr-4"
          />
        </div>
        <nav>
          <ul className="flex space-x-6 items-center">
            <li>
              <a href="#" className="flex items-center hover:underline">
                <FaBook className="mr-2" /> Docs
              </a>
            </li>
            <li>
              <a href="#" className="flex items-center hover:underline">
                <FaGithub className="mr-2" /> Visit us on GitHub
              </a>
            </li>
            <li>
              <a href="#" className="flex items-center hover:underline">
                <FaCreativeCommons className="mr-2" /> License
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </footer>
    </div>
  );
};

export default PublicationDetails;